// Vendor
import { FC, useEffect, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

// Components
import Gap from 'src/components/atoms/Gap';
import { Icon } from 'src/components/atoms/Icon';
import { Row } from 'src/components/atoms/Row';
import { ToolTip } from 'src/components/atoms/ToolTip';
import { ToolTipDirection } from 'src/components/atoms/ToolTip/ToolTip';
import { Filter, SelectedFilterData } from 'src/components/organisms/Filter';
import InputSearch from 'src/components/organisms/InputSearch';
import { SelectedSortData, Sort } from 'src/components/organisms/Sort';
import { PropertyMultiSelector } from '../PropertyMultiSelector';
import { defaultValues } from './constants';
import { Container } from './styles';
import useGetSelectedData from './useGetSelectedData';

// Types
import { CheckboxData } from 'src/components/atoms/Checkbox/types';

// Hooks
import usePropertiesDDown from 'src/features/property/hooks/usePropertiesDDown';
import { useRentVerificationInfo, useSetRentVerification } from 'src/features/rentVerification';

export type IFormValues = {
  properties?: CheckboxData[];
};

const FDEInboxToolbar: FC = () => {
  const { filters, isToolbarDisabled } = useRentVerificationInfo();
  const { filtering, sorting, searching, clearSearch, clearAllFilters } = useSetRentVerification();
  const { selectedFilterData, selectedSortData, buildFilterQuery } = useGetSelectedData();
  const justClearedRef = useRef(false);

  const { onResetGetPropertiesDDown } = usePropertiesDDown();

  const methods = useForm<IFormValues>({
    mode: 'onSubmit',
    defaultValues: {
      properties: undefined
    }
  });

  useEffect(() => {
    return () => {
      onResetGetPropertiesDDown();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = (value?: string) => {
    searching(value || undefined);
  };

  const handleClearSearch = () => {
    clearSearch();
  };

  const handleFilter = (selected: SelectedFilterData) => {
    const query = buildFilterQuery(selected) || undefined;

    filtering(query);
  };

  const handleClearAllFilters = () => {
    justClearedRef.current = true;
    clearAllFilters();
    methods.reset();
    setTimeout(() => {
      justClearedRef.current = false;
    }, 100);
  };

  const handleSort = (selected: SelectedSortData) => {
    const query = selected?.field ? `${selected?.field}:${selected?.order}` : undefined;

    sorting(query);
  };

  return (
    <Container>
      <Row alignItems="center" gap={1.5} marginLeft="-10rem">
        <Row.Col size={3}>
          <InputSearch
            name="search"
            value={filters?.q || ''}
            placeholder="Search by applicant name"
            onSearch={({ value }) => handleSearch(value as string)}
            onClear={handleClearSearch}
            isDisabled={isToolbarDisabled}
          />
        </Row.Col>
        <Row.Col size={2}>
          <FormProvider {...methods}>
            <PropertyMultiSelector
              methods={methods}
              isDisabled={isToolbarDisabled}
              onCallback={({ data: properties }) => {
                methods.setValue('properties', properties);
              }}
              onApply={() => {
                if (justClearedRef.current) {
                  return;
                }

                const properties = methods
                  .getValues('properties')
                  ?.map((property) => property.value)
                  .join('|');

                const selected = { ...selectedFilterData, properties: properties || undefined };

                handleFilter(selected);
              }}
            />
          </FormProvider>
        </Row.Col>
        <Row.Col size={1} alignContent="left">
          <ToolTip
            direction={ToolTipDirection.bottom_fit}
            content="Use the properties filter to narrow your search to specific properties. 'Select All' to search across all properties you have access to."
          >
            <Icon icon="help" />
          </ToolTip>
        </Row.Col>
      </Row>
      <Gap height={1} />
      <Row alignItems="center" gap={1.5} marginLeft="-10rem">
        <Filter
          filterText="Filter will be applied only to rent verification tab."
          data={defaultValues.filterPanel}
          selected={selectedFilterData}
          onSelect={(selected) => {
            // Get current properties from form state to avoid using stale selectedFilterData
            const currentProperties = methods
              .getValues('properties')
              ?.map((property) => property.value)
              .join('|');

            handleFilter({
              properties: currentProperties || selectedFilterData.properties,
              ...selected
            });
          }}
          onClearAll={handleClearAllFilters}
          isDisabled={isToolbarDisabled}
        />
        <Sort
          data={defaultValues.sortPanel}
          selected={selectedSortData}
          onSelect={handleSort}
          isDisabled={isToolbarDisabled}
        />
        <ToolTip
          direction={ToolTipDirection.bottom_fit}
          content="Sorting and filtering will be applied only to the rent verification tab."
        >
          <Icon icon="help" />
        </ToolTip>
      </Row>
    </Container>
  );
};

export default FDEInboxToolbar;
